import React, { useEffect } from 'react';

import SEO from '../SEO';import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { CheckCircle, ArrowRight, Shield, Search, Wrench, Clock, Target, BarChart3 } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const SmartProgramOverview = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const smartPillars = [
    {
      letter: 'S',
      title: 'Systematic Inspections',
      description: 'Regular, thorough assessments of your equipment to identify issues before they become failures.',
      icon: Search,
      path: '/maintenance/systematic-inspections'
    },
    {
      letter: 'M',
      title: 'Material-Specific Solutions',
      description: 'Customized repair approaches using the right materials for each unique application.',
      icon: Target,
      path: '/maintenance/material-specific-solutions'
    },
    {
      letter: 'A',
      title: 'Active Corrosion Control',
      description: 'Proactive protection strategies that stop corrosion before it causes damage.',
      icon: Shield,
      path: '/maintenance/active-corrosion-control'
    },
    {
      letter: 'R',
      title: 'Reliable Repair Methods',
      description: 'Proven techniques backed by decades of industrial experience and field success.',
      icon: Wrench,
      path: '/maintenance/reliable-repair-methods'
    },
    {
      letter: 'T',
      title: 'Total Asset Lifecycle',
      description: 'Comprehensive management from installation through end-of-life optimization.',
      icon: Clock,
      path: '/maintenance/total-asset-lifecycle'
    }
  ];

  const benefits = [
    'Reduce unplanned downtime by up to 70%',
    'Extend equipment life by 3-5x typical lifespan',
    'Lower total maintenance costs by 25-40%',
    'Improve safety and compliance',
    'Predictable maintenance budgeting',
    'Expert support and consultation'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title="SMART Maintenance Program" description="BCS SMART preventive maintenance program. Systematic inspections and active corrosion control for industrial assets." path="/maintenance/smart-program" />
      
      <PageHero
        badge="Preventative Maintenance"
        title="SMART Program Overview"
        description="Our comprehensive SMART approach to asset protection combines systematic inspections, material expertise, and proven repair methods to maximize equipment reliability and minimize costs."
        backgroundKey="smart-program"
        buttonText="Schedule a Consultation"
      />

      {/* SMART Pillars Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-4 text-center">The SMART Approach</h2>
          <p className="text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            Each component of our SMART program works together to provide comprehensive protection for your industrial assets.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {smartPillars.map((pillar, index) => {
              const IconComponent = pillar.icon;
              return (
                <Card 
                  key={index} 
                  className="cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-l-green-700"
                  onClick={() => navigate(pillar.path)}
                >
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <span className="w-12 h-12 bg-green-700 text-white rounded-full flex items-center justify-center text-xl font-bold mr-3">
                        {pillar.letter}
                      </span>
                      <div>
                        <span className="text-lg">{pillar.title}</span>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-4">{pillar.description}</p>
                    <span className="text-green-700 font-medium flex items-center">
                      Learn More <ArrowRight className="ml-1" size={16} />
                    </span>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Program Benefits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start p-4 bg-white rounded-lg shadow-sm">
                <CheckCircle className="text-green-700 mr-3 mt-1 flex-shrink-0" size={20} />
                <p className="text-gray-700">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-green-700 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold mb-2">40+</div>
              <div className="text-green-100">Years Experience</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">5,000+</div>
              <div className="text-green-100">Projects Completed</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">70%</div>
              <div className="text-green-100">Downtime Reduction</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">98%</div>
              <div className="text-green-100">Customer Satisfaction</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Get SMART?</h2>
          <p className="text-blue-100 mb-8">
            Let us assess your facility and develop a customized preventative maintenance plan.
          </p>
          <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => navigate('/#contact')}>
            Get In Touch <ArrowRight className="ml-2" size={20} />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SmartProgramOverview;
